/**
 * ResultList Component
 */

import { environment } from '../../../../relay';
import { instantSearchFragment, instantSearchRefetchQuery } from '../../../../relay/instantSearch';
import { getTaxonomyTitle, TaxonomyType } from '../../../../relay/selectors/taxonomy';
import { RelayOperationBase } from '../../../../utils/relay';

import { find, memoize } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { createRefetchContainer, QueryRenderer } from 'react-relay';
import { createSelector } from 'reselect';

import NProgress from '../../../atoms/feedback/NProgress';
import { Span } from '../../../atoms/text/Label';
import { CategoryResultList, InstantSearchResultCategory } from './CategoryResultList';
import { ResultLinkArtist, ResultLinkTrack } from './ResultLink';
import { ResultLinkFilter } from './ResultLinkFilter';

interface IOwnProps {
  className: string;
  query: string;
  relay: IRelayProps<ITrackSearchQueryVariables>;
  viewer: GQL.IViewer | null;
  onResults?(): void;
}

const defaultList = [];
const getInstantSearch = ({ viewer }: IOwnProps) => viewer ? viewer.trackInstantSearch : null;
const getInstantSearchResult = createSelector(
  getInstantSearch,
  (instantSearch) => memoize((type: InstantSearchResultCategory) => instantSearch ? instantSearch[type] : defaultList),
);

const getInstantSearchTaxonomyResults = createSelector(
  getInstantSearchResult,
  (selector) => memoize((type: TaxonomyType) => {
    const taxonomies = selector('taxonomies') as GQL.ITaxonomy[];
    const taxonomy = find(taxonomies, ['name', type]);
    if (taxonomy) {
      const { terms, ...rest } = taxonomy;
      const termsWithTaxonomy: GQL.ITaxonomyTerm[] = (terms || []).map((term) => ({
        ...term,
        taxonomy: {
          ...rest,
          terms: [],
        },
      }));
      return termsWithTaxonomy;
    }
    return defaultList;
  }),
);

export class InternalResultList extends React.Component<IOwnProps> {
  results: {
    tracks: GQL.ITrack[];
    artists: GQL.IArtist[];
    moods: GQL.ITaxonomyTerm[];
    genres: GQL.ITaxonomyTerm[];
    lyrics: GQL.ITaxonomyTerm[];
    references: GQL.ITaxonomyTerm[];
    industries: GQL.ITaxonomyTerm[];
    instruments: GQL.ITaxonomyTerm[];
    catalogs: GQL.ITaxonomyTerm[];
    vocals: GQL.ITaxonomyTerm[];
    languages: GQL.ITaxonomyTerm[];
  };

  renderLinkFilter(label: string, term: GQL.ITaxonomyTerm, isTaxonomyQuery: boolean = false) {
    const filter: SearchFilter = {
      name: term.name!,
      taxonomy: {
        id: term.taxonomy!.internal_id!,
        name: term.taxonomy!.name!,
      },
    };

    return (
      <ResultLinkFilter
        filter={filter}
        isTaxonomyQuery={isTaxonomyQuery}
        label={label}
        {...this.getClassName()('link')}
      />
    );
  }

  render() {
    const classes = this.getClassName();

    const {
      tracks,
      artists,
      moods,
      genres,
      lyrics,
      references,
      industries,
      instruments,
      catalogs,
      vocals,
      languages,
    } = this.getResultsByCategory();

    const props = {
      ...classes('category-result-list'),
    };

    return (
      <div {...classes()}>
        <NProgress active={!!!this.props.viewer} />
        <CategoryResultList
          key={`${this.props.query}#moods`}
          index={0}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Moods)}
          results={moods}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#genres`}
          index={1}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Genres)}
          results={genres}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#industries`}
          index={2}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Industries)}
          results={industries}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#instruments`}
          index={3}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Instruments)}
          results={instruments}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#references`}
          index={4}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.References)}
          results={references}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#vocals`}
          index={5}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Vocals)}
          results={vocals}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#languages`}
          index={6}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Languages)}
          results={languages}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#lyrics`}
          index={7}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Lyrics)}
          results={lyrics}
          {...props}
        >
          {(term) => {
            // IMPORTANT: lyrics should actually be searched by the query the user entered
            // and NOT the value of the clicked lyric
            const label = term.name!;
            term.name = this.props.query;
            return this.renderLinkFilter(label, term, true);
          }}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#tracks`}
          index={8}
          category="tracks"
          title="Track Titles"
          results={tracks}
          {...props}
        >
          {({ artist, slug, title }) => (
            <ResultLinkTrack label={`${title} by ${artist!.name}`} slug={slug!} {...classes('link')} />
          )}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#artists`}
          index={9}
          category="artists"
          title="Artists"
          results={artists}
          {...props}
        >
          {({ name, slug }) => (
            <ResultLinkArtist label={name!} slug={slug!} {...classes('link')} />
          )}
        </CategoryResultList>
        <CategoryResultList
          key={`${this.props.query}#catalogs`}
          index={10}
          category="taxonomies"
          title={getTaxonomyTitle(TaxonomyType.Catalogs)}
          results={catalogs}
          {...props}
        >
          {(term) => this.renderLinkFilter(term.name!, term)}
        </CategoryResultList>
      </div>
    );
  }

  getClassName() {
    return new BEM(this.props.className);
  }

  getResultsByCategory() {
    const allResults = getInstantSearchResult(this.props);
    this.results = {
      tracks: allResults('tracks') as GQL.ITrack[],
      artists: allResults('artists') as GQL.IArtist[],
      moods: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Moods),
      genres: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Genres),
      lyrics: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Lyrics),
      references: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.References),
      industries: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Industries),
      instruments: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Instruments),
      catalogs: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Catalogs),
      vocals: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Vocals),
      languages: getInstantSearchTaxonomyResults(this.props)(TaxonomyType.Languages),
    };

    return this.results;
  }
}

const RelayContainer = createRefetchContainer(
  InternalResultList,
  instantSearchFragment,
  instantSearchRefetchQuery,
);

type IResultListQueryRendererProps = Pick<IOwnProps, 'className' | 'query'>;

export const ResultList = (ownProps: IResultListQueryRendererProps) => (
  <QueryRenderer<RelayOperationBase<{ query: string; }, IOwnProps>>
    environment={environment}
    query={instantSearchRefetchQuery}
    variables={{ query: ownProps.query }}
    render={({ error, props }) => {
      if (error) {
        return <Span size="small" textColor="dangerous">{error.message}</Span>;
      }

      return (
        <RelayContainer
          {...(props ? props : { viewer: null })}
          {...ownProps}
        />
      );

      // return (
      //   <div className={ownProps.className}>
      //     <NProgress active />
      //   </div>
      // );
    }}
  />
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/global/InstantSearch/ResultList.tsx